import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';
import { parseCreditAdjudicatorMessage } from './parseCreditAdjudicatorMessage';
import type { ReviewReasonEvidence } from './reviewReason.types';

export type AiLinkageAdjudicatorView = {
  issueStatement: string;
  dataPoints: ReviewReasonEvidence[];
  footerNote: string | null;
  advisoryLabel: string | null;
  advisorySeverity: 'warning' | 'success' | 'default';
};

const LOOKS_LIKE_LABELS: Record<string, string> = {
  EMPLOYER_PAYROLL: 'Employer payroll label',
  PAYROLL_PROVIDER: 'Payroll processor',
  GOVERNMENT_SCHEME: 'Government benefit',
  PERSONAL_TRANSFER: 'Personal transfer',
  UNKNOWN: 'Unrecognized payor',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function humanizeToken(value: string): string {
  return value
    .split(/[_/\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function parseAdvisoryFromReason(reason: string): {
  suggested: 'PASS' | 'REVIEW';
  confidencePct: number | null;
} | null {
  const match = reason.match(/\(Advisory\s+(PASS|REVIEW),\s*(\d+)%\)/i);
  if (!match?.[1]) return null;
  const suggested = match[1].trim().toUpperCase();
  if (suggested !== 'PASS' && suggested !== 'REVIEW') return null;
  return {
    suggested,
    confidencePct: match[2] ? Number.parseInt(match[2], 10) : null,
  };
}

export function extractBankPayorFromLinkageReason(reason: string): string | null {
  const quoted =
    reason.match(/bank transaction "([^"]+)"/i)?.[1]?.trim() ??
    reason.match(/bank transaction '([^']+)'/i)?.[1]?.trim();
  return quoted || null;
}

export function extractEmployerFromLinkageReason(reason: string): string | null {
  return (
    reason.match(/Employer "([^"]+)"/i)?.[1]?.trim() ??
    reason.match(/employer "([^"]+)"/i)?.[1]?.trim() ??
    null
  );
}

/** OPG-style one-line issue for payroll linkage popover header. */
export function buildPayrollLinkageIssueStatement(input: {
  employerName: string | null;
  bankPayor: string | null;
  looksLike: string | null;
  matchAssessment: string | null;
  creditMessage: string | null;
}): string {
  const sections = parseCreditAdjudicatorMessage(input.creditMessage);
  if (sections.caseSummary) return sections.caseSummary;

  const employer = input.employerName?.trim();
  const payor = input.bankPayor?.trim();

  if (employer && payor) {
    if (input.matchAssessment === 'CONSISTENT') {
      return `Bank deposit "${payor}" appears consistent with declared employer "${employer}" — confirm this is payroll, not a personal or benefit payment.`;
    }
    if (input.matchAssessment === 'INCONSISTENT') {
      return `Bank deposit "${payor}" does not clearly match declared employer "${employer}" — verify whether the memo is an abbreviation, payroll processor, or a different payor.`;
    }
    return `Bank deposit "${payor}" versus declared employer "${employer}" needs a closer look before treating income as employment payroll.`;
  }

  if (input.looksLike === 'PERSONAL_TRANSFER') {
    return 'The deposit pattern looks like a personal transfer rather than employer payroll.';
  }
  if (input.looksLike === 'GOVERNMENT_SCHEME') {
    return 'The deposit may be a government benefit rather than employer payroll.';
  }

  return 'Review the bank payor line against the declared employer before confirming payroll linkage.';
}

function linkageEvidence(evidence: Record<string, unknown> | null): Record<string, unknown> {
  if (!evidence) return {};
  const nested = evidence.gateEvidence;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const linkage = (nested as Record<string, unknown>).LINKAGE;
    if (linkage && typeof linkage === 'object' && !Array.isArray(linkage)) {
      return { ...evidence, ...(linkage as Record<string, unknown>) };
    }
  }
  const top = evidence.LINKAGE;
  if (top && typeof top === 'object' && !Array.isArray(top)) {
    return { ...evidence, ...(top as Record<string, unknown>) };
  }
  return evidence;
}

function buildDataPoints(input: {
  employerName: string | null;
  bankPayor: string | null;
  looksLike: string | null;
  matchAssessment: string | null;
}): ReviewReasonEvidence[] {
  const rows: ReviewReasonEvidence[] = [];
  if (input.bankPayor) {
    rows.push({ label: 'Bank payor (memo)', value: input.bankPayor, tone: 'observed' });
  }
  if (input.employerName) {
    rows.push({ label: 'Declared employer', value: input.employerName, tone: 'declared' });
  }
  if (input.looksLike) {
    rows.push({
      label: 'Payor type (AI)',
      value: LOOKS_LIKE_LABELS[input.looksLike] ?? humanizeToken(input.looksLike),
      tone: 'aiInsight',
    });
  }
  if (input.matchAssessment) {
    rows.push({
      label: 'Name alignment (AI)',
      value: humanizeToken(input.matchAssessment),
      tone: 'aiInsight',
    });
  }
  return rows.slice(0, 4);
}

/** Structured adjudicator view for AI Linkage Analysis checkpoint popover. */
export function buildAiLinkageAdjudicatorView(input: {
  reason: string;
  detail: EmployerVerificationDetail;
  footprint?: EmployerFootprintAdminViewDto | null;
}): AiLinkageAdjudicatorView {
  const reason = input.reason.trim();
  const evidence = asRecord(input.detail.evidence);
  const linkage = linkageEvidence(evidence);
  const ai = asRecord(linkage.aiLinkageAnalysis ?? evidence?.aiLinkageAnalysis);

  const creditMessage =
    readString(ai?.creditAdjudicatorMessage) ??
    reason.match(/Adjudicator:\s*(.+)$/is)?.[1]?.trim() ??
    null;

  const employerName =
    readString(input.detail.employer?.legalName) ??
    extractEmployerFromLinkageReason(reason) ??
    readString(asRecord(evidence?.requestSnapshot)?.declaredEmployerName);

  const bankPayor =
    readString(linkage.bankTransactionString) ??
    readString(evidence?.bankTransactionString) ??
    extractBankPayorFromLinkageReason(reason) ??
    creditMessage?.match(/memo "([^"]+)"/i)?.[1]?.trim() ??
    null;

  const looksLike = readString(ai?.looksLike)?.toUpperCase() ?? null;
  const matchAssessment = readString(ai?.matchAssessment)?.toUpperCase() ?? null;

  const suggestedFromAi = readString(ai?.suggestedOutcome)?.toUpperCase();
  const confidenceFromAi =
    typeof ai?.confidence === 'number' && Number.isFinite(ai.confidence)
      ? Math.round(ai.confidence * 100)
      : null;

  const advisoryFromReason = parseAdvisoryFromReason(reason);
  const suggested =
    suggestedFromAi === 'PASS' || suggestedFromAi === 'REVIEW'
      ? suggestedFromAi
      : advisoryFromReason?.suggested ?? null;
  const confidence = confidenceFromAi ?? advisoryFromReason?.confidencePct ?? null;

  const advisoryLabel =
    suggested === 'REVIEW' || suggested === 'PASS'
      ? `Advisory ${suggested}${confidence != null ? ` · ${confidence}% confidence` : ''}`
      : null;

  return {
    issueStatement: buildPayrollLinkageIssueStatement({
      employerName,
      bankPayor,
      looksLike,
      matchAssessment,
      creditMessage,
    }),
    dataPoints: buildDataPoints({
      employerName,
      bankPayor,
      looksLike,
      matchAssessment,
    }),
    footerNote:
      'Automated payroll linkage pre-read — does not change the verification outcome.',
    advisoryLabel,
    advisorySeverity:
      suggested === 'PASS' ? 'success' : suggested === 'REVIEW' ? 'warning' : 'default',
  };
}
